export const conceptsData = [
  {
    id: "concept-1",
    number: "01",
    name: "Bold Urban Activism",
    theme: "The Prophetic Spark / Living Movement",
    archetype: "The Civic Disruptor & Welcoming Fire",
    tagline: "Bold Faith. Radical Love. Real Chicago.",
    secondaryTaglines: [
      "The Church Has Left the Building.",
      "Faith That Doesn't Flinch.",
      "Unapologetically Queer-Affirming. Anti-Racist. Jesus-Rooted.",
      "No Dogma. No Gatekeepers. Just Love in Action."
    ],
    summary: "An energetic, fearless, and civic-forward visual identity that positions Urban Village Church as Chicago's premier progressive spiritual movement. Merging wheatpaste street art energy with prophetic moral clarity.",
    emotionalTone: "Fearless, electric, energetic, unapologetic, kinetic, courageous",
    audienceFit: "Civic organizers, Gen-Z / Millennials, queer community, deconstructing Christians craving moral clarity without dogmatic gatekeeping.",
    strategicPillars: [
      { title: "Unapologetic Moral Courage", desc: "Anti-racist, LGBTQIA+ celebrating theology stated boldly in public streetscapes and digital arenas." },
      { title: "The Third Place for Activists", desc: "Bridging spiritual reflection with tangible grassroots civic action across Chicago neighborhoods." },
      { title: "Deconstruction-Safe Space", desc: "Honoring righteous anger at institutional religious harm; zero spiritual bypassing." }
    ],
    fonts: {
      display: "Space Grotesk (700 Bold / 800 ExtraBold)",
      body: "Plus Jakarta Sans (400 Regular / 500 Medium)",
      mono: "JetBrains Mono (600 SemiBold Uppercase)"
    },
    cssTokens: {
      "--accent-primary": "#3B28CC",
      "--accent-secondary": "#FFB300",
      "--accent-coral": "#FF3D71",
      "--accent-green": "#00C853",
      "--bg-card-border": "#3B28CC",
      "--hero-font": "'Space Grotesk', sans-serif"
    },
    colors: [
      { name: "Electric Indigo", hex: "#3B28CC", rgb: "rgb(59, 40, 204)", hsl: "hsl(247, 67%, 48%)", role: "Primary Authority & Anchor", textDark: false },
      { name: "Radiant Marigold", hex: "#FFB300", rgb: "rgb(255, 179, 0)", hsl: "hsl(42, 100%, 50%)", role: "Energy, Joy & Primary Action", textDark: true },
      { name: "Prophetic Coral", hex: "#FF3D71", rgb: "rgb(255, 61, 113)", hsl: "hsl(344, 100%, 62%)", role: "Urgent Alerts & Spark", textDark: false },
      { name: "Vivid Emerald", hex: "#00C853", rgb: "rgb(0, 200, 83)", hsl: "hsl(145, 100%, 39%)", role: "Ecological Justice & Hope", textDark: true },
      { name: "Void Obsidian", hex: "#0F1117", rgb: "rgb(15, 17, 23)", hsl: "hsl(225, 21%, 7%)", role: "Deep Background Surface", textDark: false },
      { name: "Clean White Tint", hex: "#F8F9FE", rgb: "rgb(248, 249, 254)", hsl: "hsl(230, 75%, 98%)", role: "Crisp Reading Canvas", textDark: true }
    ],
    logos: {
      mark: "/logos/c1-logo-mark.svg",
      horizontal: "/logos/c1-logo-horizontal.svg",
      subSites: [
        { name: "Masterbrand", label: "URBAN VILLAGE CHURCH", tag: "ALL CHICAGO SITES", color: "#FFB300" },
        { name: "HPW", label: "URBAN VILLAGE // HPW", tag: "6323 S. COTTAGE GROVE", color: "#FF3D71" },
        { name: "Ravenswood", label: "URBAN VILLAGE // RAVENSWOOD", tag: "4511 N. HERMITAGE", color: "#00C853" },
        { name: "Wicker Park", label: "URBAN VILLAGE // WICKER PARK", tag: "1202 N. MILWAUKEE", color: "#FFB300" },
        { name: "River Forest", label: "URBAN VILLAGE // RIVER FOREST", tag: "7970 LAKE ST", color: "#FFB300" },
        { name: "Online Campus", label: "URBAN VILLAGE // EVERYWHERE", tag: "LIVE STREAM & GROUPS", color: "#3B28CC" }
      ]
    },
    mockups: [
      {
        id: "c1-street",
        title: "Street & Civic Presence",
        subtitle: "Wicker Park Sidewalk A-Frame & Street Pole Banners",
        image: "/mockups/c1-street-mockup.jpg",
        description: "Heavy-duty matte black steel sidewalk A-frame and avenue pole banners with high-impact kinetic typography and wheatpaste background textures."
      },
      {
        id: "c1-worship",
        title: "Sunday Liturgy & Worship",
        subtitle: "16:9 LED Sanctuary Projection Slide",
        image: "/mockups/c1-worship-slide.jpg",
        description: "Modern worship staging with ultra-high contrast dark obsidian projection screens, angular motion graphics, and bold legible lyrics."
      },
      {
        id: "c1-merch",
        title: "Community Merch & Welcome Swag",
        subtitle: "Black Canvas Tote, Enamel Pins & Welcome Kit",
        image: "/mockups/c1-merch-collateral.jpg",
        description: "Heavyweight recycled black canvas tote, Chicago star enamel pins, activist button badges, and vibrant welcome cards on urban concrete."
      },
      {
        id: "c1-social",
        title: "Digital & Social Campaign",
        subtitle: "High-Impact Instagram Editorial Post",
        image: "/mockups/c1-social-mockup.jpg",
        description: "High-conversion Instagram carousel graphic featuring 'The Church Has Left the Building' headline and Chicago neighborhood action CTA."
      }
    ],
    sermonSeries: [
      {
        title: "Faith in the Streets: Prophetic Justice in Chicago",
        tagline: "When beliefs become boots on the pavement.",
        scripture: "Micah 6:8 & Luke 4:18-19",
        topics: [
          "Tearing Down Redlines: Housing Justice and Spiritual Reparations",
          "Holy Rage & Sacred Healing: Processing Civic Burnout",
          "The Table on the Block: Mutual Aid as Radical Communion",
          "A City Re-imagined: Collective Liberation in Chicago"
        ]
      },
      {
        title: "Unlearning the Harm: Healing from Religious Dogma",
        tagline: "Reclaiming Jesus from the empire.",
        scripture: "Galatians 5:1 & John 8:32",
        topics: [
          "Questions Are Not Sins: Rebuilding Faith After Fundamentalism",
          "Queer Divinity: Celebrating the LGBTQIA+ Image of God",
          "Faith Beyond Fear: Why Perfect Love Casts Out Shame"
        ]
      }
    ],
    samplePostCopy: "What if church wasn't where you hid from the city, but where you organized for it? For 14+ years in Chicago, Urban Village Church has stood in uncompromising solidarity with queer siblings, immigrant neighbors, and everyone seeking a faith that does not flinch. Sundays across HPW, Ravenswood, Wicker Park, River Forest, and Online.",
    sidewalkQuote: "DOUBTERS, SEEKERS, QUEER SAINTS & TIRED ACTIVISTS: YOU HAVE A HOME HERE. Urban Village Church // Sundays 10:00 AM."
  },
  {
    id: "concept-2",
    number: "02",
    name: "Grounded Sanctuary",
    theme: "The Urban Hearth / Rooted Village",
    archetype: "The Restorative Caregiver & Table Host",
    tagline: "A Sanctuary for Seekers, Doubters, and Dreamers.",
    secondaryTaglines: [
      "In a Hurried City, Find Space to Breathe.",
      "You Belong at the Table — Exactly as You Are.",
      "Faith Rooted in Love. Community Rooted in Place.",
      "Come Exhale."
    ],
    summary: "A warm, organic, and restorative visual identity focused on human-scale hospitality, trauma healing, contemplative depth, and deep neighborhood roots in Chicago.",
    emotionalTone: "Warm, restorative, contemplative, grounded, intimate, gentle, peaceful",
    audienceFit: "Young families, burnout professionals, spiritual seekers recovering from religious trauma, seekers craving quiet depth, tactile warmth, and genuine connection.",
    strategicPillars: [
      { title: "Restorative Hospitality", desc: "Worship and community designed as an antidote to urban exhaustion and hustle culture." },
      { title: "The Open Table as Hearth", desc: "Centering communion, shared neighborhood meals, and cross-generational friendships." },
      { title: "Trauma-Informed Spiritual Care", desc: "Gentle, non-manipulative language that honors the deconstruction journey with tenderness." }
    ],
    fonts: {
      display: "Fraunces (600 Soft Serif / Medium Italic)",
      body: "Plus Jakarta Sans (400 Regular / 500 Medium)",
      mono: "Courier Prime / Outfit (400 Tactile Grotesque)"
    },
    cssTokens: {
      "--accent-primary": "#C85A32",
      "--accent-secondary": "#2D4A3E",
      "--accent-coral": "#D99B26",
      "--accent-green": "#A2B29F",
      "--bg-card-border": "#C85A32",
      "--hero-font": "'Fraunces', Georgia, serif"
    },
    colors: [
      { name: "Warm Terracotta", hex: "#C85A32", rgb: "rgb(200, 90, 50)", hsl: "hsl(16, 60%, 49%)", role: "Hearth Warmth & Core Anchor", textDark: false },
      { name: "Forest Slate", hex: "#2D4A3E", rgb: "rgb(45, 74, 62)", hsl: "hsl(155, 24%, 23%)", role: "Grounded Depth & Calm", textDark: false },
      { name: "Earthen Ochre", hex: "#D99B26", rgb: "rgb(217, 155, 38)", hsl: "hsl(39, 70%, 50%)", role: "Warm Lantern Light & Joy", textDark: true },
      { name: "Soft Sage", hex: "#A2B29F", rgb: "rgb(162, 178, 159)", hsl: "hsl(111, 12%, 66%)", role: "Gentle Growth & Renewal", textDark: true },
      { name: "Deep Bark", hex: "#1F1A17", rgb: "rgb(31, 26, 23)", hsl: "hsl(23, 15%, 11%)", role: "High-Legibility Reading Text", textDark: false },
      { name: "Warm Linen", hex: "#FBF8F2", rgb: "rgb(251, 248, 242)", hsl: "hsl(40, 50%, 97%)", role: "Gentle Canvas Background", textDark: true }
    ],
    logos: {
      mark: "/logos/c2-logo-mark.svg",
      horizontal: "/logos/c2-logo-horizontal.svg",
      subSites: [
        { name: "Masterbrand", label: "Urban Village Church", tag: "ALL CHICAGO SITES", color: "#C85A32" },
        { name: "HPW", label: "Urban Village Church • HPW", tag: "6323 S. COTTAGE GROVE", color: "#C85A32" },
        { name: "Ravenswood", label: "Urban Village Church • Ravenswood", tag: "4511 N. HERMITAGE", color: "#2D4A3E" },
        { name: "Wicker Park", label: "Urban Village Church • Wicker Park", tag: "1202 N. MILWAUKEE", color: "#D99B26" },
        { name: "River Forest", label: "Urban Village Church • River Forest", tag: "7970 LAKE ST", color: "#C85A32" },
        { name: "Online Campus", label: "Urban Village Church • The Living Room", tag: "ONLINE GATHERING", color: "#A2B29F" }
      ]
    },
    mockups: [
      {
        id: "c2-street",
        title: "Street & Civic Presence",
        subtitle: "Natural White Oak Chalkboard A-Frame Sign",
        image: "/mockups/c2-street-mockup.jpg",
        description: "Solid oak timber sidewalk sign with chalkboard inlay and warm terracotta branding along tree-lined Chicago neighborhood brick sidewalks."
      },
      {
        id: "c2-worship",
        title: "Sunday Liturgy & Worship",
        subtitle: "Warm Acoustic Sanctuary & Candlelit Table",
        image: "/mockups/c2-worship-slide.jpg",
        description: "Contemplative worship space with warm terracotta arch projection slides, candlelight communion table, and serene ambient lighting."
      },
      {
        id: "c2-merch",
        title: "Community Merch & Welcome Swag",
        subtitle: "Unbleached Linen Tote, Ceramic Mug & Seed Cards",
        image: "/mockups/c2-merch-collateral.jpg",
        description: "Natural flax linen tote bag with botanical emblem, handcrafted terracotta ceramic mug, kraft journal, and plantable wildflower seed cards."
      },
      {
        id: "c2-social",
        title: "Digital & Social Campaign",
        subtitle: "Contemplative Editorial Wellness Post",
        image: "/mockups/c2-social-mockup.jpg",
        description: "Warm terracotta and sage social graphic reading 'REST IS HOLY: In a hurried city, find space to breathe' with botanical framing."
      }
    ],
    sermonSeries: [
      {
        title: "The Art of Holy Rest: Resisting Hustle in the Windy City",
        tagline: "Spiritual practices for a non-anxious life.",
        scripture: "Matthew 11:28-30 & Psalm 23",
        topics: [
          "Unplugging the Anxiety Machine: Sabbath as Sacred Resistance",
          "The Grace of Enough: Finding Peace Beyond Productivity Metrics",
          "Silence in the Loop: Inner Contemplation Amidst Urban Noise",
          "Gathered at Dusk: The Healing Power of Table Fellowship"
        ]
      },
      {
        title: "Mending the Broken Net: Healing Our Connection to Faith",
        tagline: "When the church let you down, but Jesus still calls.",
        scripture: "John 21:1-14 & 1 Kings 19",
        topics: [
          "Honoring the Scars: Validating Religious Trauma and Spiritual Grief",
          "The God Who Listens: Prayer When You Don't Know What You Believe",
          "Re-weaving the Village: Intergenerational Community as Medicine"
        ]
      }
    ],
    samplePostCopy: "REST IS HOLY. In a city that measures your worth by your calendar and hustle, we invite you to an hour of unforced presence. No spiritual performance. No judgment. Just warm tea, acoustic music, candlelight, and open arms. There is always room at our table this Sunday across HPW, Ravenswood, Wicker Park, River Forest, and Online.",
    sidewalkQuote: "TAKE A BREATH. YOU ARE SAFE HERE. Urban Village Church // Sundays across HPW, Ravenswood, Wicker Park, River Forest, Online. A sanctuary of warmth, inclusivity, and quiet grace."
  },
  {
    id: "concept-3",
    number: "03",
    name: "Dynamic Neo-Liturgical",
    theme: "The Open Cathedral / Sacred Modernism",
    archetype: "The Sacred Architect & Illuminated Mystic",
    tagline: "Ancient Liturgy. Contemporary Minds.",
    secondaryTaglines: [
      "Sacred Wonder. Radical Table.",
      "Where Faith Meets Architecture, Art, and Mystery.",
      "An Expansive Liturgy for an Inclusive City.",
      "The Sacred in the Concrete."
    ],
    summary: "An elevated, high-design sacred-modernist identity reimagining ancient Christian liturgical beauty and open communion for contemporary urban thinkers and creatives.",
    emotionalTone: "Transcendent, architectural, intellectual, luminous, refined, reverent",
    audienceFit: "Intellectual seekers, artists, creatives, architecture enthusiasts, mainline Christians craving liturgical beauty and philosophical depth without rigid dogma.",
    strategicPillars: [
      { title: "Sacred Modernism", desc: "Elevated design, architectural purity, and profound liturgical depth for contemporary minds." },
      { title: "The Open Table as Centerpiece", desc: "Every service centers on the radical, boundary-breaking table of communion for all." },
      { title: "Intellectual & Theological Expansiveness", desc: "Engaging faith through art, philosophy, ethical inquiry, and holy mystery." }
    ],
    fonts: {
      display: "Cinzel (600 Architectural Display / 700)",
      body: "Inter (300 Light / 400 Regular / 500 Medium)",
      mono: "JetBrains Mono / Space Mono (500 Coordinate Accent)"
    },
    cssTokens: {
      "--accent-primary": "#D4AF37",
      "--accent-secondary": "#00E5FF",
      "--accent-coral": "#FF4081",
      "--accent-green": "#48C0D8",
      "--bg-card-border": "#D4AF37",
      "--hero-font": "'Cinzel', serif"
    },
    colors: [
      { name: "Deep Sanctuary Navy", hex: "#0C162C", rgb: "rgb(12, 22, 44)", hsl: "hsl(221, 57%, 11%)", role: "Sacred Contemplative Void", textDark: false },
      { name: "Luminous Brass / Gold", hex: "#D4AF37", rgb: "rgb(212, 175, 55)", hsl: "hsl(46, 65%, 52%)", role: "Divine Light & Grace", textDark: true },
      { name: "Prismatic Cyan", hex: "#00E5FF", rgb: "rgb(0, 229, 255)", hsl: "hsl(186, 100%, 50%)", role: "Stained Glass Refraction", textDark: true },
      { name: "Prismatic Magenta", hex: "#FF4081", rgb: "rgb(255, 64, 129)", hsl: "hsl(340, 100%, 62%)", role: "Liturgical Wonder Accent", textDark: false },
      { name: "Midnight Obsidian", hex: "#050811", rgb: "rgb(5, 8, 17)", hsl: "hsl(225, 55%, 4%)", role: "Architectural Canvas Void", textDark: false },
      { name: "Alabaster Stone", hex: "#F8F9FA", rgb: "rgb(248, 249, 250)", hsl: "hsl(210, 17%, 98%)", role: "Pristine Liturgical Stock", textDark: true }
    ],
    logos: {
      mark: "/logos/c3-logo-mark.svg",
      horizontal: "/logos/c3-logo-horizontal.svg",
      subSites: [
        { name: "Masterbrand", label: "URBAN VILLAGE CHURCH", tag: "ALL CHICAGO SITES", color: "#D4AF37" },
        { name: "HPW", label: "URBAN VILLAGE CHURCH — HPW", tag: "6323 S. COTTAGE GROVE", color: "#FF4081" },
        { name: "Ravenswood", label: "URBAN VILLAGE CHURCH — RAVENSWOOD", tag: "4511 N. HERMITAGE", color: "#48C0D8" },
        { name: "Wicker Park", label: "URBAN VILLAGE CHURCH — WICKER PARK", tag: "1202 N. MILWAUKEE", color: "#00E5FF" },
        { name: "River Forest", label: "URBAN VILLAGE CHURCH — RIVER FOREST", tag: "7970 LAKE ST", color: "#D4AF37" },
        { name: "Online Campus", label: "URBAN VILLAGE CHURCH — DIGITAL PARISH", tag: "LITURGY ONLINE", color: "#0C162C" }
      ]
    },
    mockups: [
      {
        id: "c3-street",
        title: "Street & Civic Presence",
        subtitle: "Architectural Bronze Cantilever Blade Sign",
        image: "/mockups/c3-street-mockup.jpg",
        description: "Machined brushed bronze and sanctuary navy exterior blade sign with stained glass light refractions on limestone masonry in Chicago River North."
      },
      {
        id: "c3-worship",
        title: "Sunday Liturgy & Worship",
        subtitle: "Modernist Sacred Sanctuary & Open Table Projection",
        image: "/mockups/c3-worship-slide.jpg",
        description: "Minimalist cathedral projection screen with luminous bronze wireframe arches, prismatic stained glass light beams, and communion table."
      },
      {
        id: "c3-merch",
        title: "Community Merch & Welcome Swag",
        subtitle: "Navy Hardcover Journal & Brass Bookmark Suite",
        image: "/mockups/c3-merch-collateral.jpg",
        description: "Sanctuary navy linen hardcover prayer journal with embossed bronze foil arch emblem, solid brass bookmark, and alabaster stationery."
      },
      {
        id: "c3-social",
        title: "Digital & Social Campaign",
        subtitle: "Sacred Modernism Geometric Social Graphic",
        image: "/mockups/c3-social-mockup.jpg",
        description: "Refined editorial Instagram post with geometric arch line art and prismatic refraction reading 'ANCIENT LITURGY. CONTEMPORARY MINDS.'"
      }
    ],
    sermonSeries: [
      {
        title: "The Sacred Table: Ancient Grace in a Modern World",
        tagline: "Reclaiming the sacrament of open communion.",
        scripture: "Isaiah 25:6 & Luke 24:30-32",
        topics: [
          "The Architecture of Grace: The Sacred Geometry of Belonging",
          "Bread Broken for the City: Food as Justice, Feast as Revolution",
          "Luminous Shadow: Finding the Divine in Grief and Doubt",
          "The Final Benediction: Going Forth into the Urban Landscape"
        ]
      },
      {
        title: "Prisms of Light: Faith in the World of Modern Art & Thought",
        tagline: "When theology engages philosophy, poetry, and architecture.",
        scripture: "Proverbs 8:22-31 & Colossians 1:15-20",
        topics: [
          "Sacred Geometry: God in the Lines and Spaces of Chicago",
          "The Art of the Question: Epistemology and Spiritual Honesty",
          "Sound & Silence: Sacred Modernism in Contemporary Worship"
        ]
      }
    ],
    samplePostCopy: "ANCIENT LITURGY. CONTEMPORARY MINDS. Communion is not a reward for the righteous; it is nourishment for the journey. Every Sunday, we gather around an open table where all are welcome to receive bread, wine, and blessing without condition. Discover sacred modernism in the city.",
    sidewalkQuote: "URBAN VILLAGE CHURCH // OPEN TABLE LITURGY. Sunday Morning Prayer & Communion — 10:00 AM. A progressive sanctuary rooted in historic faith and inclusive love."
  }
];

export const comparisonCriteriaData = [
  {
    category: "Strategic & Audience Resonance",
    items: [
      {
        dimension: "Primary Target Audience",
        c1: "Gen Z & Millennials, LGBTQIA+ organizers, civic activists, deconstructing Christians",
        c2: "Young families, burnout professionals, trauma recovery seekers, unhurried seekers",
        c3: "Intellectual seekers, creatives, artists, architects, mainline progressive Christians"
      },
      {
        dimension: "Core Archetype",
        c1: "The Civic Disruptor & Welcoming Fire",
        c2: "The Restorative Caregiver & Table Host",
        c3: "The Sacred Architect & Illuminated Mystic"
      },
      {
        dimension: "Anti-Trope Distinctiveness",
        c1: "10 / 10 (Wheatpaste poster, kinetic angles, electric contrast)",
        c2: "9.0 / 10 (Botanical linocut, handcrafted ceramics, tactile linen)",
        c3: "9.5 / 10 (Architectural arches, prismatic refractions, museum-grade navy & gold)"
      }
    ]
  },
  {
    category: "Design System & Accessibility",
    items: [
      {
        dimension: "WCAG 2.1 Contrast Rating",
        c1: "AAA Pass (8.7:1 - 11.4:1 across all key pairings)",
        c2: "AAA Pass (8.4:1 - 14.2:1 across all key pairings)",
        c3: "AAA Pass (8.2:1 - 16.1:1 across all key pairings)"
      },
      {
        dimension: "Typography Strategy",
        c1: "Space Grotesk + Plus Jakarta Sans + JetBrains Mono",
        c2: "Fraunces Soft Serif + Plus Jakarta Sans + Courier Prime",
        c3: "Cinzel Display + Inter + Space Mono"
      },
      {
        dimension: "Multi-Site Neighborhood Scalability",
        c1: "9.5 / 10 (High modularity, rapid digital & vinyl rollout)",
        c2: "9.0 / 10 (Chalkboard & warm timber easily localized)",
        c3: "8.5 / 10 (High-end architectural finishes require consistent buildout standards)"
      }
    ]
  },
  {
    category: "Worship, Liturgy & Production",
    items: [
      {
        dimension: "Sunday Sanctuary Adaptation",
        c1: "High energy, LED projection, kinetic lyrics & modern worship",
        c2: "Candlelight, acoustic intimacy, open communion & restorative pause",
        c3: "Choral depth, sacred silence, architectural beauty & sacramental communion"
      },
      {
        dimension: "Digital & Social Media Reach",
        c1: "Highest organic shareability; stops the feed immediately",
        c2: "Aesthetic wellness/lifestyle pacing; excellent for contemplative reels",
        c3: "High editorial prestige, theological quote cards, architectural carousels"
      },
      {
        dimension: "Production & Implementation Cost",
        c1: "Low to Moderate (Standard screenprinting, vinyl signage)",
        c2: "Moderate (Linen textiles, stoneware mugs, seed paper)",
        c3: "Moderate to High (Foil stamping, solid brass signage, custom lighting)"
      }
    ]
  }
];
